import { useEffect, useRef, useState, type FC } from "react";

interface RecordingVideoPanelProps {
  /** Presigned URL of the recording's source video. */
  videoUrl: string;
  /**
   * The shared audio player's media element for the active recording. The video
   * is slaved to it: because the recording's `audio.m4a` was extracted from this
   * same video, the two share a clock, so following the audio element's
   * play/pause/seek/rate keeps the picture in sync. The video is muted — the
   * audio comes from the shared player — so there's no double audio and the
   * existing control bar (scrub, rate, keyboard, transcript sync) stays the
   * single source of control.
   */
  nativeAudio: HTMLMediaElement | null;
}

// Re-seek the video only when it drifts past this from the audio clock, so
// normal playback (both advancing at the same rate) doesn't stutter from
// constant currentTime writes.
const SYNC_DRIFT_THRESHOLD_S = 0.25;

export const RecordingVideoPanel: FC<RecordingVideoPanelProps> = (props) => {
  const { videoUrl, nativeAudio } = props;
  const videoRef = useRef<HTMLVideoElement>(null);
  // The stored video's codec/container may not be playable in the browser (e.g.
  // .mkv, or HEVC) even though audio extraction + transcription succeeded. Show
  // a message instead of a blank black box when the element can't decode it.
  const [hasError, setHasError] = useState(false);

  // Retry on a fresh source (e.g. a re-signed URL, or switching recordings).
  useEffect(() => {
    setHasError(false);
  }, [videoUrl]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !nativeAudio) {
      return;
    }

    const syncTime = () => {
      if (
        Math.abs(video.currentTime - nativeAudio.currentTime) >
        SYNC_DRIFT_THRESHOLD_S
      ) {
        video.currentTime = nativeAudio.currentTime;
      }
    };

    const onPlay = () => {
      syncTime();
      // Muted video, so programmatic play is allowed by autoplay policies.
      void video.play().catch(() => undefined);
    };
    const onPause = () => {
      video.pause();
      syncTime();
    };
    const onRateChange = () => {
      video.playbackRate = nativeAudio.playbackRate;
    };

    // Adopt the audio element's current state on mount / source change.
    video.playbackRate = nativeAudio.playbackRate;
    syncTime();
    if (!nativeAudio.paused) {
      void video.play().catch(() => undefined);
    }

    nativeAudio.addEventListener("play", onPlay);
    nativeAudio.addEventListener("pause", onPause);
    nativeAudio.addEventListener("seeking", syncTime);
    nativeAudio.addEventListener("timeupdate", syncTime);
    nativeAudio.addEventListener("ratechange", onRateChange);

    return () => {
      nativeAudio.removeEventListener("play", onPlay);
      nativeAudio.removeEventListener("pause", onPause);
      nativeAudio.removeEventListener("seeking", syncTime);
      nativeAudio.removeEventListener("timeupdate", syncTime);
      nativeAudio.removeEventListener("ratechange", onRateChange);
    };
    // `hasError` is a dep so that when the video errors out (and unmounts), this
    // effect re-runs and unbinds its listeners from the now-detached element.
  }, [nativeAudio, videoUrl, hasError]);

  return (
    <div className="flex justify-center border-b border-border-light bg-background p-2">
      {hasError ? (
        <div className="flex h-24 items-center px-4 text-center text-sm text-muted-foreground">
          This video cannot be played in the browser. The transcript and audio
          are still available.
        </div>
      ) : (
        <video
          ref={videoRef}
          src={videoUrl}
          muted
          playsInline
          onError={() => setHasError(true)}
          // Native controls are hidden: the shared audio bar drives playback and
          // this element only mirrors it. Clicking it would desync, so it's inert.
          className="max-h-64 w-auto rounded-md bg-black"
        />
      )}
    </div>
  );
};
